/**
 * Company role picker.
 *
 * Offers a company's ACTIVE roles (sorted by level) and preselects its default
 * role once they load. Shared by the invite page and the Create User form so the
 * two ways of bringing someone into a company agree on what is choosable.
 */

import React from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lognai/synapse-design-system-v2';
import { useRoles } from '../../hooks/use-role-hooks';

export interface CompanyRoleSelectProps {
  companyId: string | undefined;
  value: string | undefined;
  onChange: (roleId: string) => void;
  /** Preselect the company's default role when nothing is chosen yet. */
  preselectDefault?: boolean;
  /** Shown as the empty-state hint when the company has no active roles. */
  emptyHint?: string;
}

export function CompanyRoleSelect({
  companyId,
  value,
  onChange,
  preselectDefault = true,
  emptyHint = "This company has no active roles. Create one first from the company's Roles tab.",
}: CompanyRoleSelectProps) {
  // The error is rendered below: a failed load is otherwise indistinguishable
  // from "this company has no roles" — both give an empty dropdown.
  const { data: roles, error, isSuccess } = useRoles(companyId ?? '');

  // The single source of truth for what the dropdown offers — shared by the
  // options, the empty state and the default-selection effect.
  const selectableRoles = React.useMemo(
    () => (roles ?? []).filter((r) => r.isActive).sort((a, b) => a.level - b.level),
    [roles]
  );

  // A role from the previously chosen company is meaningless in this one.
  React.useEffect(() => {
    if (value && isSuccess && !selectableRoles.some((r) => r.id === value)) onChange('');
  }, [value, isSuccess, selectableRoles, onChange]);

  React.useEffect(() => {
    if (preselectDefault && !value && selectableRoles.length) {
      const defaultRole = selectableRoles.find((r) => r.isDefault) || selectableRoles[0];
      if (defaultRole) onChange(defaultRole.id);
    }
  }, [preselectDefault, value, selectableRoles, onChange]);

  return (
    <>
      <Select value={value || undefined} onValueChange={onChange} disabled={!companyId}>
        <SelectTrigger>
          <SelectValue placeholder={companyId ? 'Select a role' : 'Choose a company first'} />
        </SelectTrigger>
        <SelectContent>
          {selectableRoles.length > 0 ? (
            selectableRoles.map((role) => (
              <SelectItem key={role.id} value={role.id}>
                {role.name}
                {role.isDefault && ' (Default)'}
              </SelectItem>
            ))
          ) : (
            // Without this the popover renders as a blank strip, which is
            // indistinguishable from a broken dropdown.
            <div className="px-2 py-3 text-sm text-muted-foreground">
              No roles available for this company
            </div>
          )}
        </SelectContent>
      </Select>
      {error && (
        <p className="text-sm text-destructive mt-1">Failed to load roles: {error.message}</p>
      )}
      {!error && isSuccess && selectableRoles.length === 0 && (
        <p className="text-sm text-destructive mt-1">{emptyHint}</p>
      )}
    </>
  );
}
